// Ship.tsx — A freighter rendered in the focused system: a small hull (fuselage + nose +
// engine glow) flying its server-authoritative physics. The client mirrors the server's
// flip-and-burn N-body integrator (shipPhysics.ts) to PREDICT the ship's state forward from
// the latest snapshot, so it moves smoothly at any time-scale: the hull points along its
// engine heading (you see it flip to brake), the engine glow brightens when burning and dims
// while coasting, and its predicted forward course is drawn as a bright trajectory line.
// Mounted INSIDE the system group (SystemDetail), so positions are relative to the star.
// Hovering shows a nametag + manifest; clicking selects the ship (opens its info pane).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { AdditiveBlending, BufferAttribute, BufferGeometry, CatmullRomCurve3, Line as ThreeLine, LineBasicMaterial, Quaternion, Vector3 } from 'three';
import type { Group, Sprite, SpriteMaterial } from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import type { Galaxy } from '../types';
import type { Ship as ShipData } from '../state/economy';
import { gameClock } from '../state/gameClock';
import { useGame } from '../state/context';
import { E2E_ENABLED, e2eRegisterBody } from '../e2e';
import { shipState, shipPath, orbitCircle, makeShipState } from './shipPhysics';
import { planetLocalPos } from './shipCourse';
import { shipName } from './shipNames';
import { apparentScale, FLOOR_NONE, FLOOR_PLANET, FLOOR_SHIP } from './apparentScale';
import { trackShip, untrackShip } from '../state/shipTrack';
import { glowTexture } from './glow';
import { ShipLabel } from './ShipLabel';
import { Bracket } from './Bracket';

const SHIP_TINT = '#8fe9d0'; // teal, distinct from stars/planets
const SHIP_R = 0.05; // hull half-length in scene units
const FORWARD = new Vector3(0, 1, 0); // hull points +Y (velocity) in model space
// The trajectory is built as a smooth Catmull-Rom SPLINE through TRAJ_CONTROL control points sampled
// along the ship's current plan, then tessellated to TRAJ_RENDER vertices. It is rebuilt only when
// the plan (leg) changes — never per snapshot — so the sampled points never slide/crawl (no flicker).
const TRAJ_CONTROL = 24;
const TRAJ_RENDER = 96;

function planetName(galaxy: Galaxy, id: string): string {
  for (const s of galaxy.systems) for (const p of s.planets) if (p.id === id) return p.name;
  return id;
}

// The ship's current trade leg as "Origin → Destination" (planet names): it is bound for
// targetId, having last departed the OTHER route endpoint.
function routeLabel(galaxy: Galaxy, ship: ShipData): string {
  const origin = ship.targetId === ship.routeA ? ship.routeB : ship.routeA;
  return `${planetName(galaxy, origin)} → ${planetName(galaxy, ship.targetId)}`;
}

// The dominant cargo as a short label, e.g. "12k ore" (or "empty hold").
function cargoSummary(cargo: Record<string, number>): string {
  let good = '';
  let qty = 0;
  for (const [g, q] of Object.entries(cargo)) {
    if (q > qty) {
      good = g;
      qty = q;
    }
  }
  if (!good || qty <= 0) return 'empty hold';
  const amt = qty >= 1000 ? `${(qty / 1000).toFixed(1)}k` : qty.toFixed(0);
  return `${amt} ${good}`;
}

interface Props {
  galaxy: Galaxy;
  ship: ShipData;
  // Game-time of the snapshot this ship state was taken at — the base the client predicts
  // forward from (the streamed posLocal/velLocal/heading are valid at this instant).
  baseTime: number;
  /** Owner colour (empire ships); civilian freighters keep the default teal. */
  tint?: string;
}

export function Ship({ galaxy, ship, baseTime, tint = SHIP_TINT }: Props) {
  const { hover, hoveredId, selectedShipId, selectShip, focusedPlanetId } = useGame();
  const viewportHeight = useThree((s) => s.size.height);
  const iconRef = useRef<Sprite>(null);
  const empire = !!ship.owner && ship.owner !== 'system';
  const [local, setLocal] = useState(false);
  const camera = useThree((s) => s.camera);
  const ref = useRef<Group>(null); // course-local position (under the system group)
  const bodyRef = useRef<Group>(null); // apparent-scale + orientation
  const engineRef = useRef<Sprite>(null); // engine glow (opacity tracks thrust)
  const trajKey = useRef(''); // rebuild the trajectory line only when this segment key changes
  const state = useMemo(makeShipState, []);
  const hn = useMemo(() => new Vector3(), []);
  const planetPos = useMemo(() => new Vector3(), []);
  const world = useMemo(() => new Vector3(), []);
  const quat = useMemo(() => new Quaternion(), []);
  const glow = useMemo(glowTexture, []);
  const trajPositions = useMemo(() => new Float32Array((TRAJ_RENDER + 1) * 3), []);
  const controlBuf = useMemo(() => new Float32Array((TRAJ_CONTROL + 1) * 3), []);
  const controlVecs = useMemo(() => Array.from({ length: TRAJ_CONTROL + 1 }, () => new Vector3()), []);
  const curve = useMemo(() => new CatmullRomCurve3(controlVecs), [controlVecs]);
  const tmpv = useMemo(() => new Vector3(), []);
  // The trajectory line (the predicted forward course), built imperatively so we can refresh
  // its geometry without re-rendering. A thin additive line reads as bright.
  const traj = useMemo(() => {
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(trajPositions, 3));
    const obj = new ThreeLine(
      geo,
      new LineBasicMaterial({ color: tint, transparent: true, opacity: 0.45, blending: AdditiveBlending, depthWrite: false, toneMapped: false }),
    );
    obj.frustumCulled = false;
    obj.raycast = () => null;
    return obj;
  }, [trajPositions]);
  const name = useMemo(() => shipName(ship), [ship.id, ship.kind]);
  const selected = selectedShipId === ship.id;
  const hovered = local || hoveredId === ship.id;
  const lit = hovered || selected;

  // Under ?e2e=1, expose this ship's scene node so the playtest can read its live
  // on-screen position. Inert in production.
  useEffect(() => {
    if (!E2E_ENABLED) return;
    e2eRegisterBody(ship.id, ref.current);
    return () => e2eRegisterBody(ship.id, null);
  }, [ship.id]);

  useEffect(() => () => untrackShip(ship.id), [ship.id]);

  // Priority -1: runs before CameraRig's follow, so a chase camera sees THIS frame's position.
  useFrame(() => {
    const g = ref.current;
    if (!g) return;
    const t = gameClock.time;
    // Predict the ship's state forward from its snapshot (mirrors the server integrator).
    if (!shipState(galaxy, ship, baseTime, t, state)) {
      state.pos.set(ship.posLocal[0], ship.posLocal[1], ship.posLocal[2]);
      state.heading.set(ship.heading[0], ship.heading[1], ship.heading[2]);
      state.thrust = false;
    }
    // A parked ship's true orbit (~3 planet radii) lies INSIDE the planet as drawn whenever the
    // planet is enlarged to its minimum on-screen size (system view). Push the ship out radially
    // to just beyond the drawn surface so it stays visible; at true scale this is a no-op.
    if (ship.phase === 'orbit') {
      const sys = galaxy.systems.find((sy) => sy.id === ship.systemId);
      const p = sys?.planets.find((pl) => pl.id === ship.originId);
      if (sys && p) {
        planetLocalPos(p, t, planetPos);
        world.set(planetPos.x + sys.position[0], planetPos.y + sys.position[1], planetPos.z + sys.position[2]);
        const large = focusedPlanetId === null || focusedPlanetId === p.id;
        const drawnR = p.radius * apparentScale(camera, world, p.radius, large ? FLOOR_PLANET : FLOOR_NONE);
        hn.copy(state.pos).sub(planetPos);
        const d = hn.length();
        const minR = drawnR * 1.45;
        if (d > 1e-9 && d < minR) state.pos.copy(planetPos).addScaledVector(hn, minR / d);
      }
    }
    g.position.copy(state.pos);
    g.getWorldPosition(world);
    trackShip(ship.id, world);
    if (iconRef.current) {
      const fov = (camera as { fov?: number }).fov ?? 50;
      const px = ((selectedShipId === ship.id ? 16 : 11) * 2 * Math.tan((fov * Math.PI) / 360)) / viewportHeight;
      iconRef.current.scale.set(px, px, 1);
    }
    const body = bodyRef.current;
    if (body) {
      // Point the hull along its engine heading — so the flip-and-burn reorientation is visible.
      if (state.heading.lengthSq() > 1e-12) {
        quat.setFromUnitVectors(FORWARD, hn.copy(state.heading).normalize());
        body.quaternion.copy(quat);
      }
      g.getWorldPosition(world);
      body.scale.setScalar(apparentScale(camera, world, SHIP_R, FLOOR_SHIP));
    }
    // Engine plume is the actual thrust readout: lit only when the sim is really burning (which
    // is when the nose is aligned and a burn is wanted) — OFF while coasting or turning.
    if (engineRef.current) {
      (engineRef.current.material as SpriteMaterial).opacity = state.thrust ? 1 : 0;
    }
    // Trajectory line — a smooth Catmull-Rom SPLINE, rebuilt only when the ship's PLAN (leg) changes,
    // NEVER per snapshot. Because it isn't resampled each snapshot, its vertices never slide/crawl,
    // so it can't flicker. Transit: the star-local planned transfer, all the way to the destination.
    // Orbit: the planet-relative circle, translated to the planet every frame so it rides the world.
    if (ship.phase === 'orbit') {
      const okey = `o|${ship.originId}|${ship.departTime}`;
      if (trajKey.current !== okey) {
        trajKey.current = okey;
        if (orbitCircle(galaxy, ship, baseTime, TRAJ_RENDER, trajPositions)) {
          traj.geometry.getAttribute('position').needsUpdate = true;
        }
      }
      const p = galaxy.systems.find((s) => s.id === ship.systemId)?.planets.find((pl) => pl.id === ship.originId);
      if (p) traj.position.copy(planetLocalPos(p, t, planetPos));
    } else {
      // Key on the LEG (origin/dest/departTime), not baseTime — so a new snapshot on the SAME leg
      // does not rebuild the line; it stays frozen, anchored at the leg's first snapshot, to its end.
      const tkey = `t|${ship.originId}|${ship.destId}|${ship.departTime}`;
      if (trajKey.current !== tkey) {
        trajKey.current = tkey;
        traj.position.set(0, 0, 0); // transit vertices are in the star-local frame
        if (shipPath(galaxy, ship, baseTime, TRAJ_CONTROL, controlBuf)) {
          for (let i = 0; i <= TRAJ_CONTROL; i++) {
            controlVecs[i].set(controlBuf[i * 3], controlBuf[i * 3 + 1], controlBuf[i * 3 + 2]);
          }
          for (let i = 0; i <= TRAJ_RENDER; i++) {
            curve.getPoint(i / TRAJ_RENDER, tmpv);
            trajPositions[i * 3] = tmpv.x;
            trajPositions[i * 3 + 1] = tmpv.y;
            trajPositions[i * 3 + 2] = tmpv.z;
          }
          traj.geometry.getAttribute('position').needsUpdate = true;
        }
      }
    }
    (traj.material as LineBasicMaterial).opacity = lit ? 0.85 : 0.4;
  }, -1);

  const onOver = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setLocal(true);
    hover(ship.id);
    document.body.style.cursor = 'pointer';
  };
  const onOut = () => {
    setLocal(false);
    hover(null);
    document.body.style.cursor = 'auto';
  };
  const onClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    selectShip(ship.id, ship.systemId); // clicking an in-system ship focuses its system
  };

  return (
    <>
      {/* Trajectory arc for the current in-system leg (system-local frame, sibling of the
          positioned hull). A thin additive line reads as a bright glowing course. */}
      <primitive object={traj} />
      <group ref={ref}>
        <group ref={bodyRef}>
          {/* Engine glow trailing behind the hull (the thruster) — its opacity tracks thrust in
              useFrame, so it flares on a burn and fades while coasting. Plus a soft halo so the
              ship reads as a bright point at any range. */}
          <sprite ref={engineRef} position={[0, -SHIP_R * 1.1, 0]} scale={[SHIP_R * 2.4, SHIP_R * 2.4, 1]} renderOrder={2}>
            <spriteMaterial map={glow} color="#bafff0" transparent opacity={0.95} depthWrite={false} blending={AdditiveBlending} toneMapped={false} />
          </sprite>
          <sprite scale={[SHIP_R * 5, SHIP_R * 5, 1]} renderOrder={1}>
            <spriteMaterial map={glow} color={tint} transparent opacity={lit ? 0.6 : 0.4} depthWrite={false} blending={AdditiveBlending} toneMapped={false} />
          </sprite>
          {/* Fuselage (tapered, wider at the engine end) + nose cone. */}
          <mesh onPointerOver={onOver} onPointerOut={onOut} onClick={onClick}>
            <cylinderGeometry args={[SHIP_R * 0.26, SHIP_R * 0.38, SHIP_R * 1.4, 12]} />
            <meshStandardMaterial color="#e8eefb" emissive="#3a5566" emissiveIntensity={0.5} roughness={0.45} metalness={0.7} />
          </mesh>
          <mesh position={[0, SHIP_R * 0.95, 0]} onPointerOver={onOver} onPointerOut={onOut} onClick={onClick}>
            <coneGeometry args={[SHIP_R * 0.26, SHIP_R * 0.55, 12]} />
            <meshStandardMaterial color="#cdd9ee" roughness={0.4} metalness={0.75} />
          </mesh>
          {/* Engine nozzle — a small bright disc at the rear. */}
          <mesh position={[0, -SHIP_R * 0.75, 0]}>
            <circleGeometry args={[SHIP_R * 0.34, 16]} />
            <meshBasicMaterial color="#bafff0" toneMapped={false} />
          </mesh>
        </group>
        {empire ? (
          // Constant on-screen dot in the owner's colour, so empire ships read at any zoom.
          <sprite ref={iconRef} renderOrder={5} raycast={() => null}>
            <spriteMaterial map={glow} color={tint} transparent opacity={0.95} depthWrite={false} depthTest={false} blending={AdditiveBlending} toneMapped={false} sizeAttenuation={false} />
          </sprite>
        ) : null}
        {lit ? <Bracket /> : null}
        <ShipLabel
          name={name}
          meta={lit ? (ship.maxHp ? `${Math.round(ship.hp ?? 0)}/${ship.maxHp} hp` : `${cargoSummary(ship.cargo)} · ${ship.pops} crew`) : undefined}
          route={lit ? (ship.maxHp ? `→ ${planetName(galaxy, ship.targetId)}` : routeLabel(galaxy, ship)) : undefined}
          lit={lit}
        />
      </group>
    </>
  );
}
